/*
 * smart-upsell.js
 * ---------------------------------------------------------------
 * Behaviour for the key-targeted cart upsell.
 *
 * Responsibilities
 *   1. Pick which rendered offer to show, based on localStorage usr_qz.
 *   2. Fill in a selling plan client-side if Liquid could not resolve one.
 *   3. Accept  -> add the upgrade, then remove the original line and set
 *                 the discount attribute in a single write, then refresh.
 *   4. Dismiss -> hide the offer for the rest of the session.
 *
 * The ONE thing to verify against this theme is config.refreshCart.
 * Everything else is self-contained.
 */
(function () {
  'use strict';

  var config = {
    defaultKey: 'default',
    dismissStorageKey: 'su_dismissed',
    /* Set to true only if the theme exposes no cart refresh hook at all. */
    reloadFallback: false,
    /* Override from theme code if the cart needs a bespoke refresh call. */
    refreshCart: null,
    /* Reserved for client-side pricing adjustments. See registerPricingHook().
       The attribute-driven discount is handled in Liquid, not here. */
    pricingHooks: {}
  };

  var busy = false;

  /* ------------------------------------------------------------------ */
  /* Storage helpers                                                     */
  /* ------------------------------------------------------------------ */

  function currentKey() {
    if (window.SmartUpsellKey && typeof window.SmartUpsellKey.get === 'function') {
      return window.SmartUpsellKey.get();
    }
    try { return localStorage.getItem('usr_qz') || ''; }
    catch (e) { return ''; }
  }

  function dismissedSet() {
    try {
      var raw = sessionStorage.getItem(config.dismissStorageKey);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch (e) {
      return new Set();
    }
  }

  function rememberDismissal(id) {
    try {
      var set = dismissedSet();
      set.add(id);
      sessionStorage.setItem(config.dismissStorageKey, JSON.stringify(Array.from(set)));
    } catch (e) { /* fail quiet */ }
  }

  /* ------------------------------------------------------------------ */
  /* Selling plan matching — mirrors the Liquid logic exactly            */
  /* ------------------------------------------------------------------ */

  function normalizePlanString(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[\s\-()]/g, '');
  }

  function intervalToken(deliveryInterval) {
    var token = normalizePlanString(deliveryInterval);
    return token.replace(/s$/, '');
  }

  function matchSellingPlan(allocations, deliveryInterval) {
    var token = intervalToken(deliveryInterval);
    if (!token || !allocations || !allocations.length) return null;

    var number = String(deliveryInterval || '').trim().split(' ')[0];

    for (var i = 0; i < allocations.length; i++) {
      var plan = allocations[i].selling_plan || {};
      var name = normalizePlanString(plan.name);

      if (name.indexOf(token) !== -1) return plan.id;
      /* Shopify sometimes names the 1-month plan "Delivery every month". */
      if (number === '1' && name.indexOf('everymonth') !== -1) return plan.id;
    }
    return null;
  }

  var productCache = {};

  function fetchSellingPlan(handle, variantId, deliveryInterval) {
    if (!handle) return Promise.resolve(null);

    var pending = productCache[handle] ||
      (productCache[handle] = fetch('/products/' + handle + '.js', {
        headers: { Accept: 'application/json' }
      }).then(function (res) {
        return res.ok ? res.json() : null;
      }).catch(function () {
        return null;
      }));

    return pending.then(function (product) {
      if (!product || !product.variants) return null;
      var variant = product.variants.find(function (v) {
        return String(v.id) === String(variantId);
      });
      if (!variant) return null;
      return matchSellingPlan(variant.selling_plan_allocations, deliveryInterval);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Choosing which offer to show                                        */
  /* ------------------------------------------------------------------ */

  function selectOffer(root) {
    var offers = Array.from(root.querySelectorAll('[data-su-offer]'));
    if (!offers.length) return null;

    var key = currentKey();
    var dismissed = dismissedSet();

    var available = offers.filter(function (offer) {
      return !dismissed.has(offer.getAttribute('data-su-dismiss-id'));
    });
    if (!available.length) return null;

    var byKey = function (wanted) {
      return available.find(function (offer) {
        return offer.getAttribute('data-su-key') === wanted;
      });
    };

    return (key && byKey(key)) || byKey(config.defaultKey) || null;
  }

  function render(root) {
    if (root.dataset.suBound !== 'true') {
      root.addEventListener('click', onClick);
      root.dataset.suBound = 'true';
    }

    root.querySelectorAll('[data-su-offer]').forEach(function (offer) {
      offer.hidden = true;
    });

    var chosen = selectOffer(root);
    if (!chosen) {
      root.hidden = true;
      return;
    }

    chosen.hidden = false;
    root.hidden = false;
    ensureSellingPlan(chosen);
  }

  function ensureSellingPlan(offer) {
    if (offer.getAttribute('data-su-force-subscription') !== 'true') return;
    if (offer.getAttribute('data-su-selling-plan-id')) return;
    if (offer.dataset.suPlanFetched === 'true') return;

    offer.dataset.suPlanFetched = 'true';

    fetchSellingPlan(
      offer.getAttribute('data-su-handle'),
      offer.getAttribute('data-su-variant-id'),
      offer.getAttribute('data-su-delivery-interval')
    ).then(function (planId) {
      if (planId) offer.setAttribute('data-su-selling-plan-id', planId);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cart mutations                                                      */
  /* ------------------------------------------------------------------ */

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw new Error(data.description || data.message || 'Cart request failed');
        return data;
      });
    });
  }

  function refreshCart() {
    if (typeof config.refreshCart === 'function') {
      return Promise.resolve(config.refreshCart());
    }

    document.dispatchEvent(new CustomEvent('smart-upsell:cart-updated'));

    /* Resolved at call time, so theme load order doesn't matter.
       This theme exposes a global refreshCart() that re-fetches
       /cart?view=ajax — same call its own line-item X button makes. */
    var hooks = [
      function () { return window.refreshCart; },
      function () { return window.ajaxCart && window.ajaxCart.refresh && window.ajaxCart.refresh.bind(window.ajaxCart); },
      function () { return window.AjaxCart && window.AjaxCart.refresh && window.AjaxCart.refresh.bind(window.AjaxCart); },
      function () { return window.theme && window.theme.cart && window.theme.cart.refresh && window.theme.cart.refresh.bind(window.theme.cart); }
    ];

    for (var i = 0; i < hooks.length; i++) {
      try {
        var fn = hooks[i]();
        if (typeof fn === 'function') { fn(); return Promise.resolve(); }
      } catch (e) { /* try the next one */ }
    }

    if (window.jQuery) {
      window.jQuery(document).trigger('ajaxCart:refresh');
      return Promise.resolve();
    }

    console.warn('[smart-upsell] No cart refresh hook found. Set SmartUpsell.config.refreshCart.');
    return Promise.resolve();
  }

  function accept(offer, root) {
    if (busy) return;
    busy = true;
    root.classList.add('is-loading');

    var variantId  = offer.getAttribute('data-su-variant-id');
    var planId     = offer.getAttribute('data-su-selling-plan-id');
    var forceSub   = offer.getAttribute('data-su-force-subscription') === 'true';
    var allowStack = offer.getAttribute('data-su-stack') !== 'false';
    var lineKey    = root.getAttribute('data-su-line-key');
    var quantity   = parseInt(root.getAttribute('data-su-quantity'), 10) || 1;

    /* Cart attribute that the Shopify Function keys its discount on.
       Empty when the offer declares no `discount.applies_attribute`. */
    var attrKey    = offer.getAttribute('data-su-attr-key');
    var attrValue  = offer.getAttribute('data-su-attr-value');

    var lineItem = { id: Number(variantId), quantity: quantity, properties: {} };

    /* stack:false marks the NEW line so its own JSON never fires. */
    if (!allowStack) lineItem.properties._su_stop = '1';

    var planReady = forceSub
      ? (planId ? Promise.resolve(planId) : fetchSellingPlan(
          offer.getAttribute('data-su-handle'),
          variantId,
          offer.getAttribute('data-su-delivery-interval')
        ))
      : Promise.resolve(null);

    planReady
      .then(function (resolvedPlanId) {
        if (forceSub) {
          if (!resolvedPlanId) {
            /* Fail closed: never add a one-time purchase where a
               subscription was required. */
            throw new Error('No selling plan resolved for variant ' + variantId);
          }
          lineItem.selling_plan = Number(resolvedPlanId);
        }
        return postJSON('/cart/add.js', { items: [lineItem] });
      })
      .then(function () {
        /* Removal + discount attribute in ONE write. Each cart write on
           this store costs ~700-1000ms because third-party listeners hold
           the request open, so merging these saves a full round trip.
           The discount function reads attributes at pricing time, so the
           attribute does not need to precede the add.
           `updates` is keyed by line item key, so only this exact line is
           removed even if the same variant appears elsewhere. */
        var payload = { updates: {} };
        payload.updates[lineKey] = 0;

        if (attrKey) {
          payload.attributes = {};
          payload.attributes[attrKey] = attrValue;
        }

        return postJSON('/cart/update.js', payload);
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('smart-upsell:accepted', {
          detail: {
            variantId: variantId,
            sellingPlanId: lineItem.selling_plan || null,
            key: offer.getAttribute('data-su-key'),
            stack: allowStack,
            attribute: attrKey ? { key: attrKey, value: attrValue } : null
          }
        }));
        return refreshCart();
      })
      .catch(function (error) {
        console.error('[smart-upsell]', error);
        root.classList.add('has-error');
      })
      .finally(function () {
        busy = false;
        root.classList.remove('is-loading');
      });
  }

  function dismiss(offer, root) {
    rememberDismissal(offer.getAttribute('data-su-dismiss-id'));
    document.dispatchEvent(new CustomEvent('smart-upsell:dismissed', {
      detail: { key: offer.getAttribute('data-su-key') }
    }));
    render(root);
  }

  /* ------------------------------------------------------------------ */
  /* Events                                                              */
  /* ------------------------------------------------------------------ */

  function onClick(event) {
    var acceptBtn = event.target.closest('[data-su-accept]');
    var dismissBtn = event.target.closest('[data-su-dismiss]');
    if (!acceptBtn && !dismissBtn) return;

    event.preventDefault();
    event.stopPropagation();

    var offer = event.target.closest('[data-su-offer]');
    var root = event.target.closest('[data-smart-upsell]');
    if (!offer || !root) return;

    if (acceptBtn) accept(offer, root);
    else dismiss(offer, root);
  }

  function renderAll(scope) {
    (scope || document).querySelectorAll('[data-smart-upsell]').forEach(render);
  }

  /* The cart drawer replaces its own markup, so re-run on any injection. */
  var observer = new MutationObserver(function (mutations) {
    var needsRender = mutations.some(function (mutation) {
      return Array.from(mutation.addedNodes).some(function (node) {
        return node.nodeType === 1 &&
          (node.matches('[data-smart-upsell]') || node.querySelector('[data-smart-upsell]'));
      });
    });
    if (needsRender) renderAll();
  });

  function start() {
    renderAll();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('smart-upsell:key-changed', function () { renderAll(); });

  /* ------------------------------------------------------------------ */
  /* Public surface                                                      */
  /* ------------------------------------------------------------------ */

  window.SmartUpsell = {
    config: config,
    refresh: renderAll,
    matchSellingPlan: matchSellingPlan,
    clearDismissals: function () {
      try { sessionStorage.removeItem(config.dismissStorageKey); } catch (e) {}
      renderAll();
    },
    /* Client-side pricing adjustments plug in here rather than in this file.
       A hook receives { offer, root, priceDelta } and returns either
       null (no change) or { priceDelta, message }. */
    registerPricingHook: function (name, fn) {
      config.pricingHooks[name] = fn;
    }
  };
})();